import EmptyState from 'components/EmptyState';
import { BarChartOutlined } from 'components/icons';
import { useState } from 'react';
import { useGetBillableHoursByYearReportQuery } from 'store/api/reportingTrends';
import { BillableHoursByYearRow, BillableHoursByYearTotals } from 'types/reporting/trends';
import { t } from 'utils/getTranslations';
import { ChartPanel } from '../charts/ChartPanel';
import { formatChange, formatWhole } from '../charts/chartFormat';
import { ChartReportSkeleton } from '../charts/ChartReportView';
import { KpiRow, KpiTile } from '../charts/KpiTile';
import { PivotSpec } from '../charts/pivotData';
import { PivotTable } from '../charts/PivotTable';
import { ReportBarChart } from '../charts/ReportBarChart';
import { sameDayLastYear, yearToDateRange } from '../charts/yearToDate';
import { formatReportNumber, pivotToneRule, ReportTone } from '../reportCells';
import { ReportEntry } from '../reportCatalog';
import { ReportLayout } from '../ReportLayout';

const TONE: ReportTone = 'yellow';

type Row = BillableHoursByYearRow;

/** One row of hours, the years across. */
const PIVOT_SPEC: PivotSpec<Row> = {
    rowKey: () => [t('reporting.column.billable-hours')],
    columnKey: (row) => `${row.year}`,
    value: (row) => row.billableHours,
};

function yearLabel(column: string): string {
    return column;
}

function BillableHoursByYearCharts({ rows, totals }: { rows: Row[]; totals: BillableHoursByYearTotals }) {
    const [today] = useState(() => new Date());
    const measure = t('reporting.column.billable-hours');
    const bars = rows.map((row) => ({ key: `${row.year}`, label: `${row.year}`, value: row.billableHours }));
    return (
        <>
            <KpiRow>
                <KpiTile
                    label={t('reporting.chart.billable-hours-ytd')}
                    value={formatWhole(totals.billableHoursYtd)}
                    caption={yearToDateRange(today)}
                />
                <KpiTile
                    label={t('reporting.chart.billable-hours-lytd')}
                    value={formatWhole(totals.billableHoursLytd)}
                    caption={yearToDateRange(sameDayLastYear(today))}
                />
                <KpiTile
                    label={t('reporting.chart.ytd-vs-lytd')}
                    value={formatChange(totals.billableHoursYtdVsLytdPct)}
                    change={totals.billableHoursYtdVsLytdPct}
                    caption={t('reporting.chart.ytd-vs-lytd-caption')}
                />
            </KpiRow>
            <ChartPanel
                title={t('reporting.billable-hours-by-year.chart')}
                aside={t('reporting.chart.total-hours', { value: formatReportNumber(totals.billableHours) })}
            >
                <ReportBarChart bars={bars} measure={measure} />
            </ChartPanel>
            <ChartPanel title={t('reporting.billable-hours-by-year.table')} flush>
                <PivotTable
                    rows={rows}
                    spec={PIVOT_SPEC}
                    rowHeaders={['']}
                    columnLabel={yearLabel}
                    label={t('reporting.billable-hours-by-year.table')}
                    tone={TONE}
                />
            </ChartPanel>
        </>
    );
}

/** The report has no filters, so it has no filter bar either: it loads once and draws its figures. */
export function BillableHoursByYearReport({ report }: { report: ReportEntry }) {
    const { data, isFetching, isError, refetch } = useGetBillableHoursByYearReportQuery();
    let body: React.ReactNode;
    if (!data && isFetching) {
        body = <ChartReportSkeleton withFigures />;
    } else if (!data || isError) {
        body = <EmptyState size="page" variant="error" icon={BarChartOutlined} onRetry={refetch} />;
    } else {
        body = (
            <div className="new:gap-(--card-gap) flex flex-col gap-4 pt-2 pb-6">
                <BillableHoursByYearCharts rows={data.content} totals={data.totals} />
            </div>
        );
    }
    return (
        <ReportLayout
            report={report}
            toneLegend={[{ label: t('reporting.column.billable-hours'), rule: pivotToneRule(TONE) }]}
        >
            {body}
        </ReportLayout>
    );
}
